"use client";

import { useEffect, useState } from "react";
import {
  DURATION_MIN,
  formatSessionDate,
  nextSessionAt,
  proximityLabel,
} from "@/lib/masterclass";

// ============================================================================
// Compte à rebours jusqu'à la prochaine masterclass (jeudi 12h30 Paris).
//   `ready` reste false au premier rendu : l'heure courante n'existe que côté
//   client, la rendre au SSR provoquerait un mismatch d'hydratation. Les
//   composants affichent un état neutre tant que `ready` est false.
// ============================================================================

export type SessionCountdown = {
  ready: boolean;
  live: boolean; // session en cours
  dateLabel: string; // « jeudi 4 septembre »
  proximity: string; // « ce jeudi » | « jeudi prochain »
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const EMPTY: SessionCountdown = {
  ready: false,
  live: false,
  dateLabel: "",
  proximity: "jeudi",
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

export function useSessionCountdown(): SessionCountdown {
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (now === null) return EMPTY;

  const start = nextSessionAt(now);
  const live = now >= start && now < start + DURATION_MIN * 60_000;
  const remaining = Math.max(0, start - now);
  const totalSeconds = Math.floor(remaining / 1000);

  return {
    ready: true,
    live,
    dateLabel: formatSessionDate(start),
    proximity: proximityLabel(now),
    days: Math.floor(totalSeconds / 86_400),
    hours: Math.floor((totalSeconds % 86_400) / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
  };
}
